import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import RoleManagement from './RoleManagement';
import OrganizationManagement from './OrganizationManagement';
import VendorInvites from './VendorInvites';
import EvaluationForms from './EvaluationForms';
import api from '../lib/api';

// Load Google Maps API (reuse if already loaded)
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export default function Admin() {
  const { users, getUsers, updateUser, deleteUser } = useApi();
  const { user } = useAuth(); // Get user to check isDeveloper
  const { isDark } = useTheme();
  const { permissions } = usePermissions();
  
  // Tab state - only show tabs the user has permission to access
  // Developers: default to organizations
  // Others: default to first available tab
  const [activeTab, setActiveTab] = useState(user?.isDeveloper ? 'organizations' : (permissions.canViewUsers ? 'users' : 'roles')); // 'organizations', 'users', 'roles', 'vendor-invites'
  
  // Organizations state (for user edit modal)
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  
  // User modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userDeleting, setUserDeleting] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [lastLoadedTab, setLastLoadedTab] = useState(null); // Track which tab we last loaded users for
  
  // Roles removed - using simple users.role column
  

  // Load users when users tab is active (only once per tab activation)
  useEffect(() => {
    // Only load if:
    // 1. We're on the users tab
    // 2. getUsers function exists
    // 3. We're not currently loading
    // 4. We haven't already loaded for this tab activation
    if (activeTab === 'users' && getUsers && !usersLoading && lastLoadedTab !== 'users') {
      setUsersLoading(true);
      getUsers()
        .then(() => {
          setLastLoadedTab('users'); // Mark that we've loaded for this tab
        })
        .catch(err => {
          console.error('Error loading users:', err);
        })
        .finally(() => {
          setUsersLoading(false);
        });
    }
    
    // Reset loaded flag when switching away from users tab
    if (activeTab !== 'users' && lastLoadedTab === 'users') {
      setLastLoadedTab(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, getUsers]); // lastLoadedTab and usersLoading intentionally excluded to prevent infinite loops
  
  // Load organizations when user modal opens (for organization selection)
  useEffect(() => {
    if (showUserModal && !organizationsLoading && organizations.length === 0) {
      setOrganizationsLoading(true);
      api.get('/api/organizations')
        .then(data => {
          setOrganizations(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error('Error loading organizations:', err);
          setOrganizations([]);
        })
        .finally(() => {
          setOrganizationsLoading(false);
        });
    }
  }, [showUserModal, organizationsLoading, organizations.length]);

  // Roles removed - using simple users.role column
  

  return (
    <div className={`p-8 h-full overflow-y-auto ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-slate-50 text-gray-900'}`}>
      <header className={`mb-6 border-b pb-6 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {user?.isDeveloper ? 'Developer Dashboard' : 'Admin Panel'}
        </h1>
        <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {user?.isDeveloper ? 'Manage All Subscriptions & Members' : 'Organization, User & File Management'}
        </p>
        
        {/* Tabs - Only show tabs user has permission to access */}
        <div className={`flex gap-1 mt-6 rounded-lg p-1 w-fit ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
          {/* Organizations Tab - Developers only */}
          {user?.isDeveloper && (
            <button
              onClick={() => setActiveTab('organizations')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'organizations' 
                  ? 'bg-blue-600 text-white' 
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <span className="mr-2">🏢</span>
              Organizations
            </button>
          )}
          
          {/* Users Tab - Admin/Manager only */}
          {permissions.canViewUsers && (
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'users' 
                  ? 'bg-blue-600 text-white' 
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <span className="mr-2">👥</span>
              Users
            </button>
          )}
          
          {/* Roles Tab - Admin/Manager/Developer */}
          {(permissions.canViewUsers || user?.isDeveloper) && (
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'roles' 
                  ? 'bg-blue-600 text-white' 
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <span className="mr-2">🔑</span>
              Roles
            </button>
          )}
          
          {/* Vendor Invites Tab - Admin/Manager only */}
          {permissions.canViewUsers && (
            <button
              onClick={() => setActiveTab('vendor-invites')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'vendor-invites' 
                  ? 'bg-blue-600 text-white' 
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <span className="mr-2">📧</span>
              Vendor Invites
            </button>
          )}
          
          {/* Evaluation Forms Tab - Admin/Manager only */}
          {permissions.canViewUsers && (
            <button
              onClick={() => setActiveTab('evaluation-forms')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'evaluation-forms' 
                  ? 'bg-blue-600 text-white' 
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <span className="mr-2">📋</span>
              Evaluation Forms
            </button>
          )}
        </div>
      </header>

      {/* Tab Content */}
      
      {/* Organizations Tab - Developers only */}
      {activeTab === 'organizations' && user?.isDeveloper && (
        <div className={`rounded-xl overflow-hidden ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <OrganizationManagement />
        </div>
      )}

      {activeTab === 'users' && permissions.canViewUsers && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Users</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage access and permissions</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          
          {usersLoading ? (
            <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 border-2 border-t-blue-500 rounded-full animate-spin mx-auto mb-4 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}></div>
              Loading users...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className={isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                  <tr>
                    <th className="px-6 py-3 font-medium">User</th>
                    <th className="px-6 py-3 font-medium">Role</th>
                    <th className="px-6 py-3 font-medium">Organization</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={`px-6 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No users found in database.
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              user.auth_provider === 'google' ? 'bg-red-900/30 text-red-400' :
                              user.auth_provider === 'microsoft' ? 'bg-blue-900/30 text-blue-400' :
                              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {user.email?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{user.email}</div>
                              <div className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {user.auth_provider === 'google' && <span>🔵 Google</span>}
                                {user.auth_provider === 'microsoft' && <span>🟦 Microsoft</span>}
                                {user.auth_provider === 'email' && <span>📧 Email</span>}
                                <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>•</span>
                                <span>{new Date(user.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.role === 'admin' ? 'bg-purple-900/30 text-purple-400' :
                            user.role === 'developer' ? 'bg-green-900/30 text-green-400' :
                            user.role === 'device' ? 'bg-amber-900/30 text-amber-400' :
                            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {user.organization_name ? (
                            <div>
                              <div>{user.organization_name}</div>
                              {user.organization_type && (
                                <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {user.organization_type}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className={`italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No organization</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded-full text-xs bg-green-900/30 text-green-400">
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button 
                            onClick={() => {
                              // Find the organization that matches the user's organization_id
                              const userOrg = organizations.find(org => org.id === user.organization_id || org.id === user.organizationId);
                              setEditingUser({
                                ...user,
                                selectedOrgId: userOrg?.id || user.organization_id || user.organizationId || null,
                                companyId: user.organization_id || user.organizationId || null, // Keep for backward compatibility
                                selectedOrgType: userOrg?.type || null
                              });
                              setShowUserModal(true);
                            }}
                            className="text-blue-500 hover:text-blue-400 cursor-pointer"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}



      {/* Roles Tab - Developer only */}
      {activeTab === 'roles' && user?.isDeveloper && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <RoleManagement />
        </div>
      )}

      {/* Vendor Invites Tab - Admin/Manager only */}
      {activeTab === 'vendor-invites' && permissions.canViewUsers && (
        <VendorInvites />
      )}

      {/* Evaluation Forms Tab - Admin/Manager only */}
      {activeTab === 'evaluation-forms' && permissions.canViewUsers && (
        <EvaluationForms />
      )}


      {/* User Edit Modal */}
      {showUserModal && editingUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Edit User</h3>
              <button 
                onClick={() => { setShowUserModal(false); setEditingUser(null); }}
                className="text-gray-400 hover:text-white text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* User Info (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <div className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-md text-gray-400">
                  {editingUser.email}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Auth Provider</label>
                <div className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-md text-gray-400 flex items-center gap-2">
                  {editingUser.auth_provider === 'google' && <span>🔵 Google</span>}
                  {editingUser.auth_provider === 'microsoft' && <span>🟦 Microsoft</span>}
                  {editingUser.auth_provider === 'email' && <span>📧 Email</span>}
                </div>
              </div>

              {/* Organization (editable) - Role is determined by organization type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Organization</label>
                <select
                  value={editingUser.selectedOrgId || ''}
                  onChange={(e) => {
                    const selectedOrgId = e.target.value;
                    const selectedOrg = organizations.find(org => org.id === selectedOrgId);
                    
                    setEditingUser(prev => ({ 
                      ...prev, 
                      selectedOrgId: selectedOrgId || null,
                      companyId: selectedOrgId || null, // Keep for backward compatibility
                      selectedOrgType: selectedOrg?.type || null
                    }));
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:border-blue-500"
                >
                  <option value="">No organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} {org.type ? `(${org.type})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select the organization this user belongs to
                </p>
              </div>

              {/* Role Assignment - Simple role dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Role <span className="text-blue-400">*</span>
                </label>
                <select
                  value={editingUser.role || ''}
                  onChange={(e) => {
                    setEditingUser(prev => ({
                      ...prev,
                      role: e.target.value
                    }));
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select a role...</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {user?.isDeveloper && <option value="developer">Developer</option>}
                  <option value="device">Device</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Current role: <span className="text-blue-400">{editingUser.role || 'Not set'}</span>
                </p>
              </div>

              {/* Developer Status (only visible to developers) */}
              {user?.isDeveloper && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingUser.is_developer || false}
                      onChange={(e) => {
                        setEditingUser(prev => ({
                          ...prev,
                          is_developer: e.target.checked
                        }));
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Developer Account
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Grants system-wide access to all companies and features. Only developers can modify this.
                  </p>
                </div>
              )}

              {/* Joined Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Joined</label>
                <div className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-md text-gray-400">
                  {new Date(editingUser.created_at).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-700 flex justify-between">
              <div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Are you sure you want to delete this user? This cannot be undone.`)) return;
                    setUserDeleting(true);
                    try {
                      await deleteUser(editingUser.id);
                      alert('User deleted successfully!');
                      setShowUserModal(false);
                      setEditingUser(null);
                    } catch (err) {
                      alert('Error deleting user: ' + err.message);
                    } finally {
                      setUserDeleting(false);
                    }
                  }}
                  disabled={userDeleting}
                  className="px-4 py-2 bg-red-900/50 hover:bg-red-900/70 text-red-300 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {userDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowUserModal(false); setEditingUser(null); }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setUserSaving(true);
                    try {
                      // Build update payload
                      const updatePayload = {};
                      
                      // If role_id is set, use new access control system
                      if (editingUser.role_id) {
                        await api.patch(`/api/users/${editingUser.id}/role`, {
                          roleId: editingUser.role_id
                        });
                      }
                      
                      // Update other fields (role, organizationId, isDeveloper) via PUT
                      if (editingUser.role !== undefined) {
                        updatePayload.role = editingUser.role;
                      }
                      // Send organizationId (backend expects this, not companyId)
                      if (editingUser.selectedOrgId !== undefined) {
                        updatePayload.organizationId = editingUser.selectedOrgId || null;
                      } else if (editingUser.companyId !== undefined) {
                        // Fallback for backward compatibility
                        updatePayload.organizationId = editingUser.companyId || null;
                      }
                      if (editingUser.is_developer !== undefined && user?.isDeveloper) {
                        updatePayload.isDeveloper = editingUser.is_developer;
                      }
                      
                      // Only call PUT if there are fields to update
                      if (Object.keys(updatePayload).length > 0) {
                        await api.put(`/api/users/${editingUser.id}`, updatePayload);
                      }
                      
                      alert('User updated successfully!');
                      
                      // Reload users to reflect changes
                      await getUsers();
                      setShowUserModal(false);
                      setEditingUser(null);
                    } catch (err) {
                      console.error('Update error:', err);
                      alert('Error updating user: ' + (err.data?.message || err.message || 'Unknown error'));
                    } finally {
                      setUserSaving(false);
                    }
                  }}
                  disabled={userSaving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {userSaving ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
